import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, LogOut } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

export default function Admin() {
    const [token, setToken] = useState(localStorage.getItem("fitmama-admin") || "");
    const [inputToken, setInputToken] = useState("");
    const [posts, setPosts] = useState([]);
    const [reels, setReels] = useState([]);
    const [newPost, setNewPost] = useState({ title: "", excerpt: "", content: "", category: "Gravidanza", image: "" });
    const [newReel, setNewReel] = useState({ caption: "", instagram_url: "", thumbnail: "" });
    const [busy, setBusy] = useState(false);

    const authHeader = { headers: { "X-Admin-Token": token } };

    const load = async () => {
        const [b, r] = await Promise.all([axios.get(`${API}/blog`), axios.get(`${API}/reels`)]);
        setPosts(b.data);
        setReels(r.data);
    };

    useEffect(() => { if (token) load(); }, [token]);

    const login = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/admin/login`, { token: inputToken });
            localStorage.setItem("fitmama-admin", inputToken);
            setToken(inputToken);
            toast.success("Accesso effettuato");
        } catch { toast.error("Token non valido"); }
    };

    const logout = () => {
        localStorage.removeItem("fitmama-admin");
        setToken("");
    };

    const addPost = async (e) => {
        e.preventDefault();
        if (!newPost.image) return toast.error("Incolla il link dell'immagine di copertina");
        setBusy(true);
        try {
            await axios.post(
                `${API}/blog`,
                { title: newPost.title, excerpt: newPost.excerpt, content: newPost.content, category: newPost.category, image_url: newPost.image },
                authHeader
            );
            setNewPost({ title: "", excerpt: "", content: "", category: "Gravidanza", image: "" });
            toast.success("Articolo aggiunto");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
        setBusy(false);
    };

    const delPost = async (id) => {
        await axios.delete(`${API}/blog/${id}`, authHeader);
        load();
    };

    const addReel = async (e) => {
        e.preventDefault();
        if (!newReel.thumbnail) return toast.error("Incolla il link dell'immagine di copertina del reel");
        setBusy(true);
        try {
            await axios.post(
                `${API}/reels`,
                { instagram_url: newReel.instagram_url, caption: newReel.caption, thumbnail_url: newReel.thumbnail },
                authHeader
            );
            setNewReel({ caption: "", instagram_url: "", thumbnail: "" });
            toast.success("Reel aggiunto");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
        setBusy(false);
    };

    const delReel = async (id) => {
        await axios.delete(`${API}/reels/${id}`, authHeader);
        load();
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-powder px-5">
                <form onSubmit={login} className="w-full max-w-sm bg-white p-8 border border-black/10" data-testid="admin-login-form">
                    <h1 className="font-serif text-3xl mb-6">Admin FitMama</h1>
                    <input
                        type="password"
                        placeholder="Admin token"
                        value={inputToken}
                        onChange={(e) => setInputToken(e.target.value)}
                        data-testid="admin-token-input"
                        className="w-full border-b border-black/30 py-3 focus:outline-none focus:border-fuchsia font-sans mb-6"
                    />
                    <button type="submit" data-testid="admin-login-btn" className="btn-fuchsia w-full justify-center">Accedi</button>
                    <Link to="/" className="block mt-6 text-xs text-ink-muted text-center editorial-link">← Home</Link>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-powder py-14 px-5 md:px-12">
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-10">
                    <h1 className="font-serif text-4xl">Admin — FitMama</h1>
                    <div className="flex gap-3">
                        <Link to="/" className="editorial-link text-sm">Home</Link>
                        <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm">
                            <LogOut size={16} /> Logout
                        </button>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-10">
                    {/* Blog */}
                    <section>
                        <h2 className="font-serif text-2xl mb-4">Articoli Blog</h2>
                        <form onSubmit={addPost} className="bg-white p-5 border border-black/10 space-y-3 mb-6" data-testid="add-post-form">
                            <input required placeholder="Titolo" value={newPost.title} onChange={(e) => setNewPost({ ...newPost, title: e.target.value })} className="w-full border-b py-2 outline-none" />
                            <input required placeholder="Excerpt breve" value={newPost.excerpt} onChange={(e) => setNewPost({ ...newPost, excerpt: e.target.value })} className="w-full border-b py-2 outline-none" />
                            <textarea placeholder="Testo completo dell'articolo (lascia una riga vuota tra i paragrafi)" value={newPost.content} onChange={(e) => setNewPost({ ...newPost, content: e.target.value })} rows={6} className="w-full border border-black/15 rounded p-2 outline-none focus:border-fuchsia text-sm" />
                            <input placeholder="Categoria (es. Gravidanza)" value={newPost.category} onChange={(e) => setNewPost({ ...newPost, category: e.target.value })} className="w-full border-b py-2 outline-none" />
                            <input required type="url" placeholder="Link immagine di copertina (https://...)" value={newPost.image} onChange={(e) => setNewPost({ ...newPost, image: e.target.value })} className="w-full border-b py-2 outline-none" />
                            <button type="submit" disabled={busy} className="btn-fuchsia !py-2 !px-4 text-sm"><Plus size={14} /> Aggiungi articolo</button>
                        </form>
                        <ul className="space-y-3">
                            {posts.map(p => (
                                <li key={p.id} className="flex justify-between items-center gap-4 bg-white p-3 border border-black/10">
                                    <img src={resolveImg(p.image_url)} alt="" className="w-14 h-14 object-cover" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-serif truncate">{p.title}</p>
                                        <p className="text-xs text-ink-muted">{p.category}</p>
                                    </div>
                                    <button onClick={() => delPost(p.id)} data-testid={`del-post-${p.id}`} className="text-fuchsia"><Trash2 size={16} /></button>
                                </li>
                            ))}
                        </ul>
                    </section>

                    {/* Reels */}
                    <section>
                        <h2 className="font-serif text-2xl mb-4">Reel Instagram</h2>
                        <form onSubmit={addReel} className="bg-white p-5 border border-black/10 space-y-3 mb-6" data-testid="add-reel-form">
                            <input required placeholder="Link Instagram (https://www.instagram.com/reel/...)" value={newReel.instagram_url} onChange={(e) => setNewReel({ ...newReel, instagram_url: e.target.value })} className="w-full border-b py-2 outline-none" />
                            <input required placeholder="Didascalia breve" value={newReel.caption} onChange={(e) => setNewReel({ ...newReel, caption: e.target.value })} className="w-full border-b py-2 outline-none" />
                            <input required type="url" placeholder="Link immagine copertina del reel (https://...)" value={newReel.thumbnail} onChange={(e) => setNewReel({ ...newReel, thumbnail: e.target.value })} className="w-full border-b py-2 outline-none" />
                            <p className="text-[11px] text-ink-muted">Suggerimento: carica lo screenshot della copertina su un servizio di immagini e incolla qui il link.</p>
                            <button type="submit" disabled={busy} className="btn-fuchsia !py-2 !px-4 text-sm"><Plus size={14} /> Aggiungi reel</button>
                        </form>
                        <ul className="space-y-3">
                            {reels.map(r => (
                                <li key={r.id} className="flex justify-between items-center gap-4 bg-white p-3 border border-black/10">
                                    <img src={resolveImg(r.thumbnail_url)} alt="" className="w-14 h-14 object-cover" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-serif truncate">{r.caption}</p>
                                        <a href={r.instagram_url} target="_blank" rel="noreferrer" className="text-xs text-ink-muted truncate block">{r.instagram_url}</a>
                                    </div>
                                    <button onClick={() => delReel(r.id)} data-testid={`del-reel-${r.id}`} className="text-fuchsia"><Trash2 size={16} /></button>
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>
            </div>
        </div>
    );
}
